import React, { useState } from "react";
import { api, getUser } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Megaphone, Send, ScrollText, ClipboardCheck } from "lucide-react";
import { ManifestoComplianceDialog } from "@/components/ManifestoComplianceDialog";

const DEFAULT_SUBJECT = "New required reading: \"Believe Before We Sell\" — the Founders' Manifesto";
const DEFAULT_BODY = `Hello,

"Believe Before We Sell" — the Founders' Manifesto, a message from the Managing Director on our journey from selling acoustic products to becoming acoustic solution partners — is now on the UniVicoustic Learning Academy.

From your next login, it will open first. Please read it end-to-end and confirm before continuing to your modules.

— UniVicoustic Learning Team`;

/** Admin card: email all employees about the manifesto update (dealers excluded). */
export const ManifestoAnnounceCard = ({ employeeCount }) => {
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState(DEFAULT_SUBJECT);
  const [body, setBody] = useState(DEFAULT_BODY);
  const [sending, setSending] = useState(null); // "test" | "all" | null
  const [confirmStep, setConfirmStep] = useState(false);
  const [complianceOpen, setComplianceOpen] = useState(false);

  const send = async (testOnly) => {
    setSending(testOnly ? "test" : "all");
    try {
      const payload = { subject, body, audience: "employees", link_path: "/learn", cta_label: "Open the Learning Portal" };
      if (testOnly) payload.test_email = getUser()?.email;
      const { data } = await api.post("/announcements/send-custom", payload);
      if (testOnly) {
        toast.success(`Test email sent to ${getUser()?.email}`);
      } else {
        toast.success(`Announcement sent to ${data.sent} of ${data.learner_count} learners`);
        setOpen(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send the announcement.");
    } finally {
      setSending(null);
    }
  };

  return (
    <>
      <Card className="p-5 border-[#E53935]/30 bg-gradient-to-r from-[#fff5f5] to-white mb-10" data-testid="manifesto-announce-card">
        <div className="flex items-center gap-4">
          <div className="shrink-0 w-11 h-11 rounded-lg bg-[#E53935]/10 flex items-center justify-center">
            <ScrollText size={20} className="text-[#E53935]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-slate-900">Founders&rsquo; Manifesto updated — &ldquo;Believe Before We Sell&rdquo;</div>
            <div className="text-sm text-slate-500">
              It now opens on every login with read-and-confirm required. Notify your learners about the addition.
            </div>
          </div>
          <Button
            variant="outline"
            data-testid="manifesto-compliance-open"
            onClick={() => setComplianceOpen(true)}
            className="shrink-0 border-[#E53935]/40 text-[#E53935] hover:bg-[#E53935]/5"
          >
            <ClipboardCheck size={15} className="mr-1.5" /> Compliance
          </Button>
          <Button
            data-testid="manifesto-announce-open"
            onClick={() => setOpen(true)}
            className="bg-[#E53935] hover:bg-[#c62f2c] text-white shrink-0"
          >
            <Megaphone size={15} className="mr-1.5" /> Notify learners
          </Button>
        </div>
      </Card>

      <ManifestoComplianceDialog open={complianceOpen} onOpenChange={setComplianceOpen} />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Notify learners — Manifesto update</DialogTitle>
            <DialogDescription>
              Emails all {employeeCount != null ? employeeCount : ""} employee learners (dealers are excluded — they don&rsquo;t see the manifesto). Edit before sending.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              data-testid="manifesto-announce-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={200}
            />
            <Textarea
              data-testid="manifesto-announce-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={10}
              maxLength={4000}
              className="text-sm"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              data-testid="manifesto-announce-test"
              disabled={!!sending}
              onClick={() => send(true)}
            >
              {sending === "test" ? "Sending…" : "Send me a test"}
            </Button>
            <Button
              data-testid="manifesto-announce-send"
              disabled={!!sending}
              onClick={() => {
                if (!confirmStep) {
                  setConfirmStep(true);
                  setTimeout(() => setConfirmStep(false), 6000);
                  return;
                }
                setConfirmStep(false);
                send(false);
              }}
              className={`${confirmStep ? "bg-[#8f1f1d] hover:bg-[#7a1a18]" : "bg-[#E53935] hover:bg-[#c62f2c]"} text-white`}
            >
              <Send size={14} className="mr-1.5" />
              {sending === "all"
                ? "Sending…"
                : confirmStep
                  ? `Really email ${employeeCount ?? "all"} learners?`
                  : "Send to all learners"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ManifestoAnnounceCard;
